import { t } from '@lingui/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BookingInfoStatus } from 'noddi-types';
import { useParams } from 'react-router-dom';

import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard } from 'noddi-ui-web';
import { ErrorPageWithTranslations } from '../../components/ErrorPageWithTrans';
import ContentWrapper from '../../components/Layouts/ContentWrapper';
import { NoddiLoadingScreenWithTrans } from '../../components/NoddiLoadingScreenWithTrans';
import { CopyReferralCodeCard } from '../PaymentInfo/CopyReferralCodeCard';
import { BgImageContainer } from './BgImageContainer';
import { BookingContentBasedOnStatus } from './BookingContentBasedOnStatus';
import { FeedbackAfterCompletedBooking } from './FeedbackAfterCompletedBooking';
import { JobCompletedAndPaymentLeft } from './JobCompletedAndPaymentLeft';

export const BookingInfo = () => {
  const { slug } = useParams<{ slug: string }>();

  const {
    data: bookingInfo,
    isPending: isBookingInfoPending,
    isSuccess: isBookingInfoSuccess,
    error: bookingInfoError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug: slug ?? '' }
  });

  const shouldRefetch = isBookingInfoSuccess && !!bookingInfo?.delayInSeconds;

  const { data: currentEstimatedArrivalTime } = noddiAsync.useGet({
    type: URLKeys.getCurrentEstimatedArrivalTime,
    input: { slug: slug ?? '' },
    queryConfig: {
      refetchInterval: shouldRefetch ? 60000 * 2 : false,
      refetchOnWindowFocus: shouldRefetch
    }
  });

  if (isBookingInfoPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (bookingInfoError) {
    return <ErrorPageWithTranslations apiError={bookingInfoError} />;
  }

  if (!bookingInfo) {
    return null;
  }

  const isInvoiced = bookingInfo.order.status === 'Invoiced';
  const amountDue = bookingInfo.order.amountDue;
  const hasPayed = isInvoiced && amountDue <= 0;
  const jobCompletedAndPaymentLeft = isInvoiced && amountDue > 0;
  // Fully completed booking that is paid for with no feedback
  const showFeedbackContent =
    bookingInfo.status === BookingInfoStatus.COMPLETED &&
    !bookingInfo.isFullyUnableToComplete &&
    !bookingInfo.feedback &&
    hasPayed;

  const getTitle = () => {
    if (showFeedbackContent) {
      return t`Thanks for using Noddi!`;
    }
    if (jobCompletedAndPaymentLeft) {
      return `${t`The job is done!`} 🎉`;
    }
    return t`Your booking`;
  };

  const Content = () => {
    if (showFeedbackContent) {
      return <FeedbackAfterCompletedBooking referralCode={bookingInfo.referralCode} bookingId={bookingInfo.id} />;
    }

    if (bookingInfo.isInvoiced) {
      return (
        <div className='flex flex-col gap-6 xxs:gap-8 lg:gap-16'>
          <NoddiBasicCard className='flex gap-2'>
            <NoddiIcon name='Check' />
            <span>{t`The payment of this booking is handled by invoice`}</span>
          </NoddiBasicCard>
          <CopyReferralCodeCard referralCode={bookingInfo.referralCode} />
        </div>
      );
    }

    if (hasPayed) {
      return (
        <div className='flex flex-col gap-6 xxs:gap-8 lg:gap-16'>
          <NoddiBasicCard className='flex gap-2'>
            <NoddiIcon name='Check' />
            <span>
              {bookingInfo.order.paymentMethod.toLowerCase().includes('invoice')
                ? t`Thanks! You will receive an invoice shortly`
                : t`Payment completed`}
            </span>
          </NoddiBasicCard>
          <CopyReferralCodeCard referralCode={bookingInfo.referralCode} />
        </div>
      );
    }

    if (jobCompletedAndPaymentLeft) {
      return <JobCompletedAndPaymentLeft bookingInfo={bookingInfo} orderId={bookingInfo.order.id} />;
    }

    return (
      <BookingContentBasedOnStatus
        status={bookingInfo.status}
        bookingInfo={bookingInfo}
        currentEstimatedArrivalTime={currentEstimatedArrivalTime}
      />
    );
  };

  return (
    <BgImageContainer>
      <ContentWrapper hideBackButton title={getTitle()}>
        <Content />
      </ContentWrapper>
    </BgImageContainer>
  );
};
